import { useState, useEffect } from "react";

interface SmartImageProps {
  src: string;
  fallbackSrc?: string;
  alt?: string;
  maxWidth?: number;
  maxHeight?: number;
  className?: string;
}

// Bootstrap breakpoints: mobile < 576, tablet < 992, desktop >= 992
const getBreakpoint = (): "mobile" | "tablet" | "desktop" => {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 576) return "mobile";
  if (w < 992) return "tablet";
  return "desktop";
};

const getResponsiveCaps = (
  maxWidth: number,
  maxHeight: number,
  bp: "mobile" | "tablet" | "desktop",
) => {
  switch (bp) {
    case "mobile":
      return {
        width: Math.round(maxWidth * 0.6),
        height: Math.round(maxHeight * 0.6),
      };
    case "tablet":
      return {
        width: Math.round(maxWidth * 0.8),
        height: Math.round(maxHeight * 0.8),
      };
    default:
      return { width: maxWidth, height: maxHeight };
  }
};

export default function SmartImage({
  src,
  fallbackSrc,
  alt = "Image",
  maxWidth = 260,
  maxHeight = 72,
  className = "",
}: SmartImageProps) {
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState(false);
  const [breakpoint, setBreakpoint] = useState<"mobile" | "tablet" | "desktop">(
    getBreakpoint,
  );

  useEffect(() => {
    const onResize = () => setBreakpoint(getBreakpoint());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Reset error when src changes
  useEffect(() => {
    setError(false);
    setDimensions(null);
  }, [src]);

  // Load the active image (fallback if primary errored)
  const activeSrc = error && fallbackSrc ? fallbackSrc : src;
  useEffect(() => {
    if (!activeSrc) return;
    setDimensions(null);

    const img = new Image();
    img.onload = () => {
      setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => setError(true);
    img.src = activeSrc;
  }, [activeSrc]);

  const { width: effMaxWidth, height: effMaxHeight } = getResponsiveCaps(
    maxWidth,
    maxHeight,
    breakpoint,
  );

  const computedStyle = (() => {
    if (!dimensions) return { width: effMaxWidth, height: effMaxHeight };

    const { width: nw, height: nh } = dimensions;
    const aspectRatio = nw / nh;

    let width = effMaxWidth;
    let height = width / aspectRatio;

    if (height > effMaxHeight) {
      height = effMaxHeight;
      width = height * aspectRatio;
    }

    return { width: Math.round(width), height: Math.round(height) };
  })();

  // Still loading — render nothing to avoid layout blink
  if (!dimensions && !error) {
    return null;
  }

  if (error && !fallbackSrc) {
    return null;
  }

  return (
    <img
      src={activeSrc}
      alt={alt}
      width={computedStyle.width}
      height={computedStyle.height}
      className={className}
      style={{
        width: computedStyle.width,
        height: computedStyle.height,
        objectFit: "contain",
        display: "block",
      }}
    />
  );
}
